'use client'
import { useEffect } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import { isAndroid, isIOS } from 'react-device-detect'
import { ORIGIN_URL } from '@/utils/url'
import { LoadingDots } from 'ui/src/components/LoadingDots/LoadingDots'
import { sprinkles } from 'ui/src/theme/sprinkles.css'

export const dynamic = 'force-dynamic'

const appStoreUrl = 'https://apps.apple.com/se/app/hedvig/id1303668531'
const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.hedvig.app'

// Redirect to the specific landing page based on the slug

function DeeplinkLandingPage() {
  const params = useParams<{ slug?: Array<string> }>()
  const searchParams = useSearchParams()

  useEffect(() => {
    const baseUrl = getRedirectBaseUrl(ORIGIN_URL)
    const targetUrl = new URL('/se/hedvig/app', baseUrl)

    //for url like /home-en or /home_app_fallback_en open english app page
    const slug = params.slug as Array<string> | undefined
    const firstSlug = slug?.[0] ?? ''
    const isMemberInsurancesDeeplink = ['insurances', 'contract'].includes(firstSlug)
    if (['home-en', 'home_app_fallback_en'].includes(firstSlug)) {
      targetUrl.pathname = '/se-en/hedvig/app'
    } else if (['direct-debit', 'connect-payment'].includes(firstSlug)) {
      targetUrl.pathname = '/se/member/payments'
    } else if (isMemberInsurancesDeeplink) {
      const contractId = searchParams.get('contractId')
      targetUrl.pathname = contractId
        ? `/se/member/insurances/${encodeURIComponent(contractId)}`
        : '/se/member/insurances'
    } else {
      targetUrl.pathname = '/se/hedvig/app'
    }
    const fallbackRedirect = () => {
      // Your post-redirect logic (e.g., analytics)
      window.location.href = targetUrl.toString()
    }

    const redirectWithFallback = (url: string) => {
      window.location.href = url
      // after redirecting to the specific url, redirect to the target URL so we don't remain on the loading
      setTimeout(fallbackRedirect, 500)
    }
    // NEXT_PUBLIC_USE_APPSTORE_LINKS are enabled for the production environment only
    // and it is used to redirect to the app store links instead of the web app page.
    // Member insurances deeplinks always go straight to the web page, never to the app store
    const shouldOpenAppStore = process.env.NEXT_PUBLIC_USE_APPSTORE_LINKS === 'true' && !isMemberInsurancesDeeplink
    if (shouldOpenAppStore) {
      // If the app store links are enabled, redirect to the app store based on the device type
      if (isIOS) {
        redirectWithFallback(appStoreUrl)
      } else if (isAndroid) {
        redirectWithFallback(playStoreUrl)
      } else {
        window.location.href = targetUrl.toString()
      }
    } else {
      window.location.href = targetUrl.toString()
    }
  }, [params.slug, searchParams])
  return (
    <div className={sprinkles({ padding: 'md' })}>
      <LoadingDots />
    </div>
  )
}

// remove the 'link.' subdomain and normalize the URL
// if the hostname has only two parts, prepend 'www.' or 'dev.' depending on the environment
function getRedirectBaseUrl(originUrl: string) {
  const url = new URL(originUrl)
  const hostname = url.hostname.replace(/^link\./, '')
  const urlPathParts = hostname.split('.')
  if (urlPathParts.length == 2) {
    let domainPrefix: string
    if (process.env.VERCEL_ENV === 'production') {
      domainPrefix = 'www'
    } else {
      domainPrefix = 'dev'
    }
    url.hostname = domainPrefix + '.' + urlPathParts.join('.')

    return url.toString()
  }
  return url.toString()
}

export default DeeplinkLandingPage
